import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { UsuarioCadastrosRequest } from 'api/Assessor/usuario';
import { AlertIcon, CardIcon, SyncIcon } from 'components/Icons';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { formatCPFOrCNPJ } from 'helpers/utils/cpfCnpjMask';
import translation from 'locales/translation';
import moment from 'moment';
import {
  Modal,
  ModalSizes,
  ScreenSize,
  Table as TableModal,
  TooltipManager,
  TooltipPosition,
} from 'plataforma-fundacao-componentes';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { assessorActionBuscarCadastros } from 'store/modules/assessor/actions';
import { tableInitialState, tableReducer } from './reducer';
import useStyle from './style';
import './styles.scss';

interface Props {
  filters: Partial<UsuarioCadastrosRequest> | undefined;
  hasDateError: boolean;
}

export default function ListagemLoginsTable({ filters, hasDateError }: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const [{ rows, size, page, count, loading }, action] = useReducer(
    tableReducer,
    tableInitialState
  );

  const heads = useMemo(
    () => [
      translation.ListaLogins.tableHead.coop,
      translation.ListaLogins.tableHead.nome,
      translation.ListaLogins.tableHead.cpf,
      translation.ListaLogins.tableHead.contas,
      translation.ListaLogins.tableHead.dataCadastro,
      translation.ListaLogins.tableHead.dataAcesso,
    ],
    []
  );

  // MODAL DE CONTAS DO USUÁRIO

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tabela = (id: number) => {
    const filter = rows.filter((x) => {
      return x.idUsuarioLogin === id;
    }) as any;

    const map = filter[0];

    return (
      <TableModal
        columns={[
          {
            key: 'relacionamento',
            value: translation.ListaLogins.modalContas.relacionamento,
            props: { style: { textAlign: 'left', fontSize: '14px' } },
          },
          {
            key: 'nomeAssociado',
            value: translation.ListaLogins.modalContas.nome,
            props: {
              style: {
                textAlign: 'left',
                fontSize: '14px',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
          },
          {
            key: 'cpfCnpj',
            value: translation.ListaLogins.modalContas.cpf,
            props: { style: { textAlign: 'left', fontSize: '14px' } },
          },
          {
            key: 'agencia',
            value: translation.ListaLogins.modalContas.agencia,
            props: { style: { textAlign: 'left', fontSize: '14px' } },
          },
          {
            key: 'nucleo',
            value: translation.ListaLogins.modalContas.nucleo,
            props: { style: { textAlign: 'left', fontSize: '14px' } },
          },
          {
            key: 'conta',
            value: translation.ListaLogins.modalContas.conta,
            props: { style: { textAlign: 'left', fontSize: '14px' } },
          },
          {
            key: 'carteira',
            value: translation.ListaLogins.modalContas.carteira,
            props: { style: { textAlign: 'left', fontSize: '14px' } },
          },
        ]}
        lines={map.contas.map((row: any) => {
          return {
            relacionamento: row.relacionamento,
            nomeAssociado:
              row.nomeAssociado.length > 22 ? (
                <>
                  <div
                    tooltip-text={row.nomeAssociado}
                    tooltip-position={TooltipPosition.Top}
                  >
                    {row.nomeAssociado}
                  </div>
                  <TooltipManager />
                </>
              ) : (
                <div>{row.nomeAssociado}</div>
              ),
            cpfCnpj: row.cpfCnpj,
            agencia: row.agencia,
            nucleo: row.nucleo,
            conta: row.conta,
            carteira: row.carteira,
          };
        })}
      />
    );
  };

  const mkeyRef = useRef<string | undefined>();
  const contasModal = useCallback(
    (title: string, id: number) => {
      mkeyRef.current = `alert${getUniqueKey()}`;
      dispatch(
        openModal(Modal, {
          preventMaskExit: false,
          size: ModalSizes.ExtraLarge,
          modalKey: mkeyRef.current,
          mobileOn: ScreenSize.xs,
          title,
          children: tabela(id),
          onClose: () => dispatch(closeModal(mkeyRef.current)),
        })
      );
    },
    [dispatch, tabela]
  );

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    value: number
  ) => {
    action({
      type: 'setPage',
      value: value + 1,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    action({
      type: 'setSize',
      value: parseInt(event.target.value, 10),
    });
    action({
      type: 'setPage',
      value: 1,
    });
  };

  useEffect(() => {
    if (!filters) return;
    action({ type: 'setLoading', value: true });
    dispatch(
      assessorActionBuscarCadastros(
        filters,
        { page, size },
        (listagem) => {
          action({ type: 'setRows', value: listagem.elements });
          action({ type: 'setCount', value: listagem.totalElements });
          action({ type: 'setLoading', value: false });
        },
        (error) => {
          ErrorHandler(error);
          action({ type: 'setLoading', value: false });
        }
      )
    );
  }, [dispatch, filters, page, size]);

  useEffect(() => {
    action({
      type: 'setPage',
      value: 1,
    });
  }, [count]);

  if (hasDateError) {
    return (
      <TableContainer className={classes.root}>
        <Typography component='p' className={classes.advice}>
          <AlertIcon />
          {translation.ListaLogins.dateErrorMessage}
        </Typography>
      </TableContainer>
    );
  }

  if (!filters) {
    return (
      <TableContainer className={classes.root}>
        <Typography component='p' className={classes.advice}>
          <AlertIcon />
          {translation.ListaLogins.advice}
        </Typography>
      </TableContainer>
    );
  }

  if (loading) {
    return (
      <TableContainer className={classes.root}>
        <Typography component='p' className={classes.advice}>
          <SyncIcon animated />
          {translation.ListaLogins.tableLoading}
        </Typography>
      </TableContainer>
    );
  }

  if (!rows.length) {
    return (
      <TableContainer className={classes.root}>
        <Typography component='p' className={classes.advice}>
          <AlertIcon />
          {translation.ListaLogins.empty}
        </Typography>
      </TableContainer>
    );
  }

  return (
    <TableContainer className={classes.root}>
      <Table>
        <TableHead className={classes.box}>
          <TableRow>
            {heads.map((head) => (
              <TableCell key={head} align='center' className={classes.title}>
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className={classes.box}>
          {rows.map((row) => (
            <TableRow key={row.idUsuarioLogin}>
              <TableCell align='center' className={classes.data}>
                {filters.coop}
              </TableCell>
              <TableCell className={classes.data}>{row.nomeLogin}</TableCell>
              <TableCell align='center' className={classes.data}>
                {formatCPFOrCNPJ(row.cpfLogin, true)}
              </TableCell>
              <TableCell align='center'>
                <IconButton
                  color='primary'
                  className={classes.contasIcon}
                  // onClick={() => {
                  //   const filter = rows
                  //     .filter((x) => {
                  //       return x.idUsuarioLogin === row.idUsuarioLogin;
                  //     })
                  //     .map((x) => {
                  //       return x.contas;
                  //     });

                  //   // const map = filter.contas.map((x) => x);

                  // }}
                  // onClick={() => openModalContas(row.idUsuarioLogin)}
                  onClick={() =>
                    contasModal(
                      translation.ListaLogins.modalContas.title,
                      row.idUsuarioLogin
                    )
                  }
                >
                  <CardIcon />
                </IconButton>
              </TableCell>
              <TableCell align='center' className={classes.data}>
                {moment(row.dataCadastro).isValid()
                  ? moment(row.dataCadastro).format('DD/MM/YYYY HH:mm:ss')
                  : '-'}
              </TableCell>
              <TableCell align='center' className={classes.data}>
                {moment(row.dataUltimoAcesso).isValid()
                  ? moment(row.dataUltimoAcesso).format('DD/MM/YYYY HH:mm:ss')
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              style={{ borderBottom: 'none' }}
              count={count}
              colSpan={9}
              rowsPerPageOptions={[10, 25, 50, 100]}
              rowsPerPage={size}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                translation.RelatorioPresentes.labelDisplayedRows(
                  from,
                  to,
                  count
                )
              }
              labelRowsPerPage={translation.RelatorioPresentes.labelRowsPerPage}
              backIconButtonProps={{ className: classes.navIcon }}
              nextIconButtonProps={{ className: classes.navIcon }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
